// Thin client for the Netlify Functions API. Same-origin in dev (via
// `netlify dev`) and prod, so relative URLs are fine.
import type { AdminStatsView, RaceMode, UsageKind } from "@shared/admin";
import type { BadgeView, ProfileView } from "@shared/profile";
import type {
  AnswerFeedback,
  Canon,
  DifficultyFilter,
  LeaderboardView,
  PublicQuestion,
  RaceProgress,
  RaceResult,
  RoomStateView,
} from "@shared/types";

const BASE = "/api";

export interface SoloConfig {
  categories: string[];
  difficulty: DifficultyFilter;
  canon: Canon;
  finishLine: number;
}

export interface StartResponse {
  token: string;
  question: PublicQuestion;
  finishLine: number;
  poolSize: number;
  progress: RaceProgress;
}

export interface SubmitResponse {
  feedback: AnswerFeedback;
  finished: boolean;
  token?: string;
  question?: PublicQuestion;
  result?: RaceResult;
  /** Server-signed proof of the finished result, for leaderboard submission. */
  resultToken?: string;
  progress: RaceProgress;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function startSolo(config: SoloConfig): Promise<StartResponse> {
  return postJson<StartResponse>("/start-solo", config);
}

export function submitAnswer(
  token: string,
  answer: string,
): Promise<SubmitResponse> {
  return postJson<SubmitResponse>("/submit-answer", { token, answer });
}

export interface CountFilters {
  categories: string[];
  difficulty: DifficultyFilter;
  canon: Canon;
}

export function questionCount(filters: CountFilters): Promise<{ count: number }> {
  return postJson<{ count: number }>("/question-count", filters);
}

// --- Daily Challenge + leaderboards ----------------------------------------

export interface DailyStartResponse extends StartResponse {
  /** UTC day (YYYY-MM-DD) of this challenge. */
  date: string;
}

/** Start today's Daily Challenge (same date-seeded set for everyone). */
export function startDaily(): Promise<DailyStartResponse> {
  return postJson<DailyStartResponse>("/start-daily", {});
}

export interface SubmitScoreResponse {
  /** 1-based placement on the board, or null if outside the ranked window. */
  rank: number | null;
  total: number;
  board: LeaderboardView;
  /** Badges newly earned by this result (empty when unlinked or none new). */
  newBadges: BadgeView[];
}

/** Post a finished result (proven by its resultToken) to its leaderboard.
 *  Pass a playerToken to link it to a profile and accrue badges. */
export function submitScore(
  resultToken: string,
  displayName: string,
  playerToken?: string,
): Promise<SubmitScoreResponse> {
  return postJson<SubmitScoreResponse>("/submit-score", {
    resultToken,
    displayName,
    playerToken,
  });
}

// --- Player profiles -------------------------------------------------------

export interface ProfileIdentity {
  playerId: string;
  playerToken: string;
  displayName: string;
  avatar: string;
  /** Set when restoring a claimed profile. */
  handle?: string | null;
}

/** Mint a new anonymous profile (called lazily on first save). */
export function profileToken(
  displayName: string,
  avatar: string,
): Promise<ProfileIdentity> {
  return postJson<ProfileIdentity>("/profile-token", { displayName, avatar });
}

/** Fetch a public profile by its id (or a claimed handle). */
export function getProfile(args: {
  playerId?: string;
  handle?: string;
}): Promise<{ profile: ProfileView }> {
  return postJson<{ profile: ProfileView }>("/get-profile", args);
}

/** Secure a profile with a handle + recovery passphrase for cross-device use. */
export function claimProfile(
  playerToken: string,
  handle: string,
  passphrase: string,
): Promise<{ handle: string }> {
  return postJson<{ handle: string }>("/claim-profile", {
    playerToken,
    handle,
    passphrase,
  });
}

/** Recover a claimed profile on a new device (re-mints a playerToken). */
export function restoreProfile(
  handle: string,
  passphrase: string,
): Promise<ProfileIdentity> {
  return postJson<ProfileIdentity>("/restore-profile", { handle, passphrase });
}

export interface DeleteProfileResponse {
  deleted: boolean;
  players: number;
  scores: number;
}

/** Delete a profile and all its data (COPPA deletion right). Authorize with
 *  EITHER the device's playerToken OR the claim handle + recovery word. */
export function deleteProfile(args: {
  playerToken?: string;
  handle?: string;
  passphrase?: string;
}): Promise<DeleteProfileResponse> {
  return postJson<DeleteProfileResponse>("/delete-profile", args);
}

export interface LeaderboardQuery {
  mode: "solo" | "daily";
  category?: string;
  difficulty?: DifficultyFilter | string;
  /** Daily only; defaults to today (UTC) server-side. */
  date?: string;
}

export function getLeaderboard(q: LeaderboardQuery): Promise<{ board: LeaderboardView }> {
  return postJson<{ board: LeaderboardView }>("/leaderboard", q);
}

// --- Admin dashboard -------------------------------------------------------

/** POST to an admin endpoint with the ADMIN_KEY secret in the `x-admin-key`
 *  header (never persisted server-side). Shares postJson's error extraction. */
async function postAdmin<T>(path: string, key: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-admin-key": key },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Fetch usage stats + the per-player table. Gated by the ADMIN_KEY secret. */
export function adminStats(key: string): Promise<{ stats: AdminStatsView }> {
  return postAdmin<{ stats: AdminStatsView }>("/admin-stats", key, {});
}

/** Admin: delete a profile by id (operator-assisted parental deletion). */
export function adminDeleteProfile(
  key: string,
  playerId: string,
): Promise<{ players: number; scores: number }> {
  return postAdmin<{ players: number; scores: number }>(
    "/admin-delete-profile",
    key,
    { playerId },
  );
}

// --- Usage analytics (aggregate, de-identified) ----------------------------

const SESSION_FLAG = "br:session-pinged";

/** Fire-and-forget increment of an aggregate usage counter. Best-effort: never
 *  blocks or surfaces errors into gameplay. `keepalive` lets the request outlive
 *  a navigation. See netlify/functions/track-usage.ts. */
export function recordUsage(kind: UsageKind): void {
  fetch(`${BASE}/track-usage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kind }),
    keepalive: true,
  }).catch(() => {
    /* best-effort analytics — ignore */
  });
}

/** Count one sit-down session — once per browser tab visit. sessionStorage
 *  survives in-tab reloads but resets for a new tab/visit, which matches "sat
 *  down to play." */
export function recordSession(): void {
  try {
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    /* sessionStorage blocked (private mode) — still count the session once */
  }
  recordUsage("session");
}

/** Count one finished game — called once per completed race.
 *
 *  Deliberately still fires ONLY from the solo/daily results screen, where it has
 *  fired since day one, so the `avgGamesPerSession` series stays comparable
 *  either side of the funnel work. Use recordRaceFinish() for the complete,
 *  per-mode picture. */
export function recordGameFinish(): void {
  recordUsage("game");
}

/** Count a mode button press on Home — the top of the funnel. */
export function recordModePick(mode: RaceMode | "leaderboard"): void {
  recordUsage(`pick:${mode}` as UsageKind);
}

/** Count a race the server actually handed back (not merely requested). */
export function recordRaceStart(mode: RaceMode): void {
  recordUsage(`start:${mode}` as UsageKind);
}

/** Count a race that reached its finish screen, per mode. Fires for all four
 *  modes, unlike the legacy `game` counter above. */
export function recordRaceFinish(mode: RaceMode): void {
  recordUsage(`finish:${mode}` as UsageKind);
}

// --- Multiplayer rooms -----------------------------------------------------

export interface CreateRoomConfig {
  nickname: string;
  categories: string[];
  difficulty: DifficultyFilter;
  canon: Canon;
  finishLine: number;
}

/** Identity + first snapshot handed back when you create or join a room. */
export interface RoomSession {
  code: string;
  raceId: string;
  playerId: string;
  /** HMAC-signed token proving you are this player; carried on every write. */
  playerToken: string;
  state: RoomStateView;
}

export interface RoomQuestionResponse {
  finished: boolean;
  question?: PublicQuestion;
  progress: RaceProgress;
  status: RoomStateView["status"];
  startedAt?: number | null;
}

export interface RoomAnswerResponse {
  feedback: AnswerFeedback;
  finished: boolean;
  raceFinished: boolean;
  question?: PublicQuestion;
  progress: RaceProgress;
}

export function createRoom(config: CreateRoomConfig): Promise<RoomSession> {
  return postJson<RoomSession>("/create-room", config);
}

/** Instant public race: you + bots, started immediately (no lobby wait). */
export function quickRace(nickname: string): Promise<RoomSession> {
  return postJson<RoomSession>("/quick-race", { nickname });
}

export function joinRoom(args: {
  code: string;
  nickname: string;
}): Promise<RoomSession> {
  return postJson<RoomSession>("/join-room", args);
}

export function startRoom(playerToken: string): Promise<{ state: RoomStateView }> {
  return postJson<{ state: RoomStateView }>("/start-room", { playerToken });
}

export function roomState(code: string): Promise<{ state: RoomStateView }> {
  return postJson<{ state: RoomStateView }>("/room-state", { code });
}

export function roomQuestion(playerToken: string): Promise<RoomQuestionResponse> {
  return postJson<RoomQuestionResponse>("/room-question", { playerToken });
}

export function roomAnswer(
  playerToken: string,
  answer: string,
): Promise<RoomAnswerResponse> {
  return postJson<RoomAnswerResponse>("/room-answer", { playerToken, answer });
}
